import { useContext } from 'react'

import { MobXProviderContext, useLocalObservable } from 'mobx-react'

import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import { sortCompaniesByTypeAndNamePredicate } from '~/client/src/shared/constants/companyType'
import { InviteStatusCaption } from '~/client/src/shared/enums/InviteStatusCaption'
import User from '~/client/src/shared/models/User'
import UserProject from '~/client/src/shared/models/UserProject'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import canSendInviteToUser from '~/client/src/shared/utils/canSendInviteToUser'
import { UNASSIGNED } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

import { IUserRow } from '../types'

interface ProvidedStores {
  projectMembersStore: ProjectMembersStore
  userProjectsStore: UserProjectsStore
  companiesStore: CompaniesStore
  state: DesktopInitialState
}

export interface UserRecordsResult {
  rows: IUserRow[]
  loading: boolean
  sortedCompanyIds: string[]
}

const PROJECT_LOADING_EVENTS = [
  e.ACTIVATE_PROJECT,
  e.GET_PROJECT_MEMBERS,
  e.GET_USERS_FROM_PROCORE,
  e.LOAD_AND_LISTEN_TO_COMPANIES,
  e.LOAD_AND_LISTEN_TO_USER_PROJECTS,
]

// Rows are derived in a MobX computed (`useLocalObservable` getters compile
// to computeds): store reads stay tracked through the `observer` caller, and
// the result keeps one reference per data version — the identity stability
// TanStack consumers and dep arrays need.
export function useUserRecords(): UserRecordsResult {
  const { projectMembersStore, userProjectsStore, companiesStore, state } =
    useContext(MobXProviderContext) as unknown as ProvidedStores

  const local = useLocalObservable(() => ({
    get derived(): Omit<UserRecordsResult, 'loading'> {
      const sorted = [...projectMembersStore.list].sort((a: User, b: User) => {
        return (
          a.lastName
            ?.toLowerCase()
            .localeCompare(b.lastName?.toLowerCase() || '') ||
          a.firstName
            ?.toLowerCase()
            .localeCompare(b.firstName?.toLowerCase() || '') ||
          0
        )
      })

      const rows: IUserRow[] = sorted.map(user => {
        const userProject = userProjectsStore.getByUser(user)
        const rolesIds = userProject?.roles || []
        const tradesIds = userProject?.trades || []
        const teamsIds = [
          ...(userProject?.teams || []),
          ...(userProject?.defaultTeams || []),
        ].filter(Boolean)
        const userTagIds = userProject?.tags || []

        let companyId = UserProject.getCompanyId(user, userProjectsStore)
        const company = companiesStore.getCompanyById(companyId, true)
        companyId = (company && company.id) || UNASSIGNED
        const companyName = company?.name || User.getDefaultCompanyName()

        const status =
          UserProject.getInviteStatusCaption(user, userProjectsStore) ||
          InviteStatusCaption.NotInvited

        return {
          id: user.id,
          user,
          companyId,
          companyName,
          fullName: user.fullName || '',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          jobTitle: user.globalRole || '',
          status,
          rolesIds,
          tradesIds,
          teamsIds,
          userTagIds,
          accountPosition: user.accountPosition || '',
          accountType: UserProject.getAccessType(user, userProjectsStore),
          email: user.email || '',
          phone: user.phoneNumber || '',
          employeeId: user.employeeId || '',
          canBeInvited: canSendInviteToUser(user, userProjectsStore),
          canBeApproved: UserProject.isPendingApproval(user, userProjectsStore),
          canBeSetNotInvited: UserProject.isAccepted(user, userProjectsStore),
        }
      })

      const byCompany = new Map<string, IUserRow[]>()
      rows.forEach(row => {
        const arr = byCompany.get(row.companyId) || []
        arr.push(row)
        byCompany.set(row.companyId, arr)
      })

      const { getCompanyById, getCompanyTypeTagsByIds } = companiesStore
      const sortedCompanyIds = Array.from(byCompany.keys()).sort((a, b) =>
        sortCompaniesByTypeAndNamePredicate(
          a,
          b,
          getCompanyById,
          getCompanyTypeTagsByIds,
        ),
      )

      const orderedRows: IUserRow[] = []
      sortedCompanyIds.forEach(cid => {
        const usersForCompany = byCompany.get(cid) || []
        usersForCompany.forEach(u => orderedRows.push(u))
      })

      return { rows: orderedRows, sortedCompanyIds }
    },
  }))

  const loading = PROJECT_LOADING_EVENTS.some(ev => state.loading.get(ev))
  const { rows, sortedCompanyIds } = local.derived

  return { rows, loading, sortedCompanyIds }
}
